/**
 * Companion Orchestrator — Vue composable
 *
 * Wrapper tipado sobre o Pinia store, com helpers para uso em SFCs.
 *
 * HLOS-38 — Sprint 3 / AC1-S3, AC3-S3.
 *
 * Constitucional:
 * - Princípio V (Aprendiz Decide): expõe pause/resume sem penalidade
 * - Princípio XI (Companion ≠ LLM): caller de Pyodide é injetado (testável)
 */

import { computed, type ComputedRef } from "vue";
import { storeToRefs } from "pinia";
import { useCompanionStore } from "../store";
import type { CompanionSnapshot, CompanionState } from "../types";

export interface UseCompanionReturn {
  /** Estado da máquina (idle | listening | thinking | responding | paused). */
  readonly state: ComputedRef<CompanionState>;
  /** Snapshot consolidado do orchestrator. */
  readonly snapshot: ComputedRef<CompanionSnapshot>;
  /** Última mensagem do Companheiro (null se sem turnos). */
  readonly hasTurn: ComputedRef<boolean>;
  /** HLOS-38 follow-up: erro atual como string (null se sem erro). */
  readonly lastError: ComputedRef<string | null>;
  /** Envia turno (não throw — caller UI decide como tratar erro). */
  readonly processTurn: ReturnType<typeof useCompanionStore>["processTurn"];
  /** Pausa (R5 — sem penalidade). */
  readonly pause: () => void;
  /** Resume de pause. */
  readonly resume: () => void;
  /** Recusa iniciativa atual (R3). */
  readonly refuseCurrentInitiative: () => void;
  /** Persiste sessão via vault (HLOS-44). */
  readonly persistSession: () => Promise<void>;
  /** Restaura sessão do vault no boot (HLOS-44). */
  readonly restoreSession: () => Promise<boolean>;
  /** Limpa lastError. */
  readonly clearError: () => void;
}

export function useCompanion(): UseCompanionReturn {
  const store = useCompanionStore();
  const { state, lastTurnAt, lastError } = storeToRefs(store);

  const snapshot = computed<CompanionSnapshot>(() => store.snapshot());

  const hasTurn = computed(() => lastTurnAt.value !== null);

  // HLOS-38 follow-up: expor refs cruciais para consumers (ConversationState).
  // lastError no store é AuthError (objeto) ou string — normaliza para string.
  const lastErrorString = computed(() => {
    const err = lastError.value;
    if (err === null) return null;
    if (typeof err === "string") return err;
    return (err as { message?: string }).message ?? String(err);
  });

  return {
    state: computed(() => state.value),
    snapshot,
    hasTurn,
    lastError: lastErrorString,
    processTurn: store.processTurn,
    pause: store.pause,
    resume: store.resume,
    refuseCurrentInitiative: store.refuseCurrentInitiative,
    persistSession: store.persistSession,
    restoreSession: store.restoreSession,
    clearError: store.clearError,
  };
}
